import React, { useEffect, useMemo } from 'react';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'react-i18next';
import useUser from 'src/hooks/useUser';
import useAuth from 'src/hooks/useAuth';
import { MultiSelectFilter } from 'src/components/Search/components';
import { mapAgents } from 'src/components/Search/utils/mapAgents';
import { CapitalizeNames } from 'src/utils/capitalize';
import type { TSelectItem } from 'src/components/Search/components/types';

interface ContactFormUsersFieldProps {
    storeId: string;
    value: TSelectItem[];
    onChange: (selected: TSelectItem[]) => void;
}

export const ContactFormUsersField = ({
    storeId,
    value,
    onChange,
}: ContactFormUsersFieldProps) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { agents, getAgents, clearUserState } = useUser();

    useEffect(() => {
        if (!storeId) {
            clearUserState();
            return;
        }

        getAgents({ stores: [storeId] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId]);

    const userOptions = useMemo(() => {
        const authUserOption: TSelectItem[] =
            user?._id && user?.name
                ? [
                      {
                          value: user._id,
                          label: CapitalizeNames(user.name),
                      },
                  ]
                : [];

        const storeUserOptions = mapAgents(agents ?? []);

        return uniqBy([...authUserOption, ...storeUserOptions], 'value');
    }, [user?._id, user?.name, agents]);

    const handleChange = (selected: TSelectItem[] | unknown) => {
        onChange(Array.isArray(selected) ? [...selected] : []);
    };

    return (
        <MultiSelectFilter
            label={String(t('contacts.users'))}
            options={userOptions}
            value={value}
            onChange={handleChange}
            placeholder={String(t('ModalFilters.placeholder.agent'))}
        />
    );
};
